import { useCallback, useEffect, useState } from 'react';

import { Spinner } from '@provider-portal/common/components/Spinner/Spinner';
import { validatePassword } from '@provider-portal/common/utils/passwordValidator';

import { Alert, Button, Field, Input } from '@bolteu/clay-react';
import { NetworkRequestState } from '@bolteu/redux-session';

interface SubmitNewPassword {
  token: string;
  password: string;
}

interface SetNewPasswordProps {
  token: string;
  strings: Record<
    | 'passwordNotMatched'
    | 'passwordMinimumRequirements'
    | 'passwordChanged'
    | 'error'
    | 'password'
    | 'confirmPassword'
    | 'setPassword'
    | 'goToLogin',
    string
  >;
  onSubmit: (payload: SubmitNewPassword) => void;
  onGoBackToLogin: () => void;
  alertMessage: string | null;
  requestState: NetworkRequestState;
}

export function SetNewPasswordForm({
  token,
  strings,
  onSubmit,
  onGoBackToLogin,
  alertMessage,
  requestState,
}: SetNewPasswordProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isErrorPassword, setIsErrorPassword] = useState(false);
  const [passwordHelperText, setPasswordHelperText] = useState('');
  const [isErrorConfirmPassword, setIsErrorConfirmPassword] = useState(false);
  const [confirmPasswordHelperText, setConfirmPasswordHelperText] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const [shouldShowErrorAlert, setShouldShowErrorAlert] = useState(false);
  const [shouldShowSuccessAlert, setShouldShowSuccessAlert] = useState(false);

  const resetFormFieldErrors = useCallback(() => {
    setIsErrorPassword(false);
    setShouldShowErrorAlert(false);
    setPasswordHelperText('');
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const validationResultErrors = validatePassword(password);
      if (!validationResultErrors) {
        onSubmit({ token, password });
        return;
      }

      setIsErrorPassword(true);
      setIsErrorConfirmPassword(true);
      setConfirmPasswordHelperText(strings.passwordMinimumRequirements);
    },
    [onSubmit, password, strings.passwordMinimumRequirements, token],
  );

  const handlePasswordChange = useCallback(
    (value: string) => {
      setPassword(value);
      resetFormFieldErrors();
    },
    [resetFormFieldErrors],
  );

  const handleGoBackToLogin = useCallback(() => {
    onGoBackToLogin();
  }, [onGoBackToLogin]);

  useEffect(() => {
    if ((!password && !confirmPassword) || password === confirmPassword) {
      setIsErrorConfirmPassword(false);
      setConfirmPasswordHelperText('');

      if (password) {
        setIsButtonDisabled(false);
      }
    }

    if (confirmPassword && password !== confirmPassword) {
      setIsButtonDisabled(true);
      setIsErrorConfirmPassword(true);
      setConfirmPasswordHelperText(strings.passwordNotMatched);
    }
  }, [password, confirmPassword, strings.passwordNotMatched]);

  useEffect(() => {
    if (alertMessage) {
      setPasswordHelperText(alertMessage);
      setIsErrorPassword(true);
      return;
    }

    if (requestState === NetworkRequestState.failure) {
      setShouldShowErrorAlert(true);
      return;
    }

    if (requestState === NetworkRequestState.success) {
      setShouldShowSuccessAlert(true);
    }

    setIsErrorPassword(false);
    setShouldShowErrorAlert(false);
    setPasswordHelperText('');
  }, [alertMessage, requestState]);

  return (
    <form onSubmit={handleSubmit} data-testid="set-password-form">
      {shouldShowErrorAlert ? (
        <div className="mt-2">
          <Alert.Root severity="error">
            <Alert.Icon />
            <Alert.Content>
              <Alert.Description>{strings.error}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
        </div>
      ) : null}
      {shouldShowSuccessAlert ? (
        <>
          <Alert.Root severity="info">
            <Alert.Icon />
            <Alert.Content>
              <Alert.Description>{strings.passwordChanged}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
          <div>
            <Button size="md" onClick={handleGoBackToLogin}>
              <span>{strings.goToLogin}</span>
            </Button>
          </div>
        </>
      ) : (
        <>
          <Field.Root invalid={isErrorPassword} className="mt-4 mb-2">
            <Field.Label>{strings.password}</Field.Label>
            <Input
              fieldSize="lg"
              id="password"
              name="password"
              type="password"
              autoComplete="off"
              onValueChange={handlePasswordChange}
            />
            {passwordHelperText ? <Field.Error match>{passwordHelperText}</Field.Error> : null}
          </Field.Root>
          <Field.Root invalid={isErrorConfirmPassword} className="mt-4 mb-2">
            <Field.Label>{strings.confirmPassword}</Field.Label>
            <Input
              fieldSize="lg"
              id="confirm_password"
              name="confirm_password"
              type="password"
              autoComplete="off"
              onValueChange={setConfirmPassword}
            />
            {confirmPasswordHelperText ? (
              <Field.Error match>{confirmPasswordHelperText}</Field.Error>
            ) : null}
          </Field.Root>
          <div className="mt-4">
            <Button type="submit" size="md" className="w-full" disabled={isButtonDisabled}>
              {requestState === NetworkRequestState.waiting ? (
                <Spinner />
              ) : (
                <span>{strings.setPassword}</span>
              )}
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
